import * as m from '@/paraglide/messages';
// import IconChrome from '@listening/assets/astro/icons/icon-chrome.svg?url';
// import IconDocument from '@listening/assets/astro/icons/icon-document.svg?url';
// import IconPasteText from '@listening/assets/astro/icons/icon-paste-text.svg?url';

// import IconWebpage from '@listening/assets/astro/icons/icon-webpage.svg?url';
// import IconWordDocs from '@listening/assets/astro/icons/icon-word-docs.svg?url';
import IconBlogs from '@listening/assets/astro/icons/icon-blogs.svg?url';
import IconDrive from '@listening/assets/astro/icons/icon-drive.svg?url';
import IconMOBIs from '@listening/assets/astro/icons/icon-mobis.svg?url';
import IconPdfUpload from '@listening/assets/astro/icons/icon-pdf-upload.svg?url';
import IconPDFs from '@listening/assets/astro/icons/icon-pdfs.svg?url';
import IconResearchPaper from '@listening/assets/astro/icons/icon-research-paper.svg?url';
// import IconPdf from '@listening/assets/astro/icons/icon-pdf.svg?url';
import IconScan from '@listening/assets/astro/icons/icon-scan.svg?url';
import IconTextbooks from '@listening/assets/astro/icons/icon-textbooks.svg?url';
import IconWebsite from '@listening/assets/astro/icons/icon-website.svg?url';
import { Predicate } from 'effect';

// Move types outside the component
type BlockPosition = {
  top: number;
  left: number;
  icon: string;
  label: string;
};

type BlockPositionMobile = BlockPosition;

// --------------------
// Reusable Components
// --------------------

// Animated gradient stop component
function AnimatedGradientStop({
  offset,
  values,
}: {
  offset: string;
  values: string;
}) {
  return (
    <stop offset={offset} stopColor='#0587FB'>
      <animate
        attributeName='stop-color'
        dur='8s'
        repeatCount='indefinite'
        values={values}
      />
    </stop>
  );
}

// SVG gradient definitions component
function GradientDefinitions({
  horizontalId,
  verticalId,
  includeMiddleStop = false,
}: {
  horizontalId: string;
  verticalId: string;
  includeMiddleStop?: boolean;
}) {
  const horizontalValues =
    '#0587FB; #FF0099; #6D4AFF; #0587FB; #00FFE0; #0587FB';
  const verticalValues = '#0587FB; #FF0099; #6D4AFF; #0587FB; #00FFE0; #0587FB';
  const middleValues = '#0587FB; #6D4AFF; #0587FB; #00FFE0; #FF0099; #0587FB';

  return (
    <defs>
      <linearGradient id={horizontalId} x1='0%' x2='100%' y1='0%' y2='0%'>
        <AnimatedGradientStop offset='0%' values={horizontalValues} />
        {includeMiddleStop && (
          <AnimatedGradientStop offset='50%' values={middleValues} />
        )}
        <AnimatedGradientStop offset='100%' values={horizontalValues} />
      </linearGradient>
      <linearGradient id={verticalId} x1='0%' x2='0%' y1='0%' y2='100%'>
        <AnimatedGradientStop offset='0%' values={verticalValues} />
        {includeMiddleStop && (
          <AnimatedGradientStop offset='50%' values={middleValues} />
        )}
        <AnimatedGradientStop offset='100%' values={verticalValues} />
      </linearGradient>
    </defs>
  );
}

// Unified Feature Block Component
type FeatureBlockProps = {
  icon: string;
  label: string;
  top: number;
  left: number | string;
  width: number | string;
  height: number;
  isDesktop?: boolean;
};

function FeatureBlock({
  icon,
  label,
  top,
  left,
  width,
  height,
  isDesktop = false,
}: FeatureBlockProps) {
  const leftValue = Predicate.isNumber(left) ? `${String(left)}px` : left;
  const widthValue = Predicate.isNumber(width) ? `${String(width)}px` : width;

  return (
    <div
      aria-label={`${label} upload option`}
      className='absolute flex items-center justify-center rounded-[24px] bg-white px-4 py-2 shadow-[0px_2px_8px_0px_rgba(0,0,0,0.05)] transition-all duration-300 ease-in-out hover:translate-y-[-2px] hover:shadow-[0px_8px_16px_0px_rgba(0,0,0,0.1)]'
      role='listitem'
      style={{
        top: `${String(top)}px`,
        left: leftValue,
        width: widthValue,
        height: `${String(height)}px`,
      }}
    >
      <img
        alt={isDesktop ? 'icon' : label}
        aria-hidden={isDesktop}
        className={`mr-2 ${isDesktop ? 'size-6 lg:size-5 xl:size-6' : 'size-6'}`}
        src={icon}
      />
      <span
        className={`font-medium whitespace-nowrap ${isDesktop ? 'text-base lg:text-sm xl:text-base' : 'text-base'}`}
      >
        {label}
      </span>
    </div>
  );
}

// --------------------
// Desktop Constants (Design: 1600px wide x DESKTOP_HEIGHT tall)
// --------------------
export function FeaturesUpload() {
  // Move all constants inside the component
  const CONTAINER_WIDTH = 1600;
  const BLOCK_WIDTH = 200;
  const BLOCK_HEIGHT = 60;
  const ROW_GAP = 180;
  const DESKTOP_HEIGHT = 3 * (BLOCK_HEIGHT + ROW_GAP) - ROW_GAP + BLOCK_HEIGHT;
  const toPercent = (value: number) =>
    `${String((value / CONTAINER_WIDTH) * 100)}%`;

  // --------------------
  // Desktop Block Positions (in PX)
  // --------------------
  const row1Gap = 180;
  const row1TotalWidth = 3 * BLOCK_WIDTH + 2 * row1Gap;
  const row1LeftOffset = (CONTAINER_WIDTH - row1TotalWidth) / 2;

  const row1Positions: [BlockPosition, BlockPosition, BlockPosition] = [
    {
      top: 0,
      left: row1LeftOffset,
      icon: IconResearchPaper,
      label: m.features_upload_research_papers(),
    },
    {
      top: 0,
      left: row1LeftOffset + BLOCK_WIDTH + row1Gap,
      icon: IconBlogs,
      label: m.features_upload_blogs(),
    },
    {
      top: 0,
      left: row1LeftOffset + 2 * (BLOCK_WIDTH + row1Gap),
      icon: IconTextbooks,
      label: m.features_upload_textbooks(),
    },
  ];

  const row2Gap = 120;
  const row2LongGap = 180;
  const row2LeftOffset =
    (CONTAINER_WIDTH - (3 * BLOCK_WIDTH + 2 * row2Gap)) / 2;

  const row2Positions: [BlockPosition, BlockPosition, BlockPosition] = [
    {
      top: BLOCK_HEIGHT + ROW_GAP,
      left: row2LeftOffset,
      icon: IconPDFs,
      label: m.features_upload_pdfs(),
    },
    {
      top: BLOCK_HEIGHT + ROW_GAP,
      left: row2LeftOffset + BLOCK_WIDTH + row2Gap,
      icon: IconMOBIs,
      label: m.features_upload_mobis(),
    },
    {
      top: BLOCK_HEIGHT + ROW_GAP,
      left: row1Positions[2].left,
      icon: IconDrive,
      label: m.features_upload_drive(),
    },
  ];

  const row3Gap = 180;
  const row3LeftOffset = row2LeftOffset;

  const row3Positions: [BlockPosition, BlockPosition] = [
    {
      top: 2 * (BLOCK_HEIGHT + ROW_GAP),
      left: row3LeftOffset,
      icon: IconWebsite,
      label: m.features_upload_websites(),
    },
    {
      top: 2 * (BLOCK_HEIGHT + ROW_GAP),
      left: row3LeftOffset + BLOCK_WIDTH + row3Gap,
      icon: IconPdfUpload,
      label: m.features_upload_upload(),
    },
  ];

  const BLOCK_POSITIONS = [
    ...row1Positions,
    ...row2Positions,
    ...row3Positions,
  ];

  // --------------------
  // Desktop Connectors (in PX)
  // --------------------
  const row1Connectors = row1Positions.slice(0, -1).map((pos) => ({
    left: pos.left + BLOCK_WIDTH,
    top: pos.top + BLOCK_HEIGHT / 2 - 1,
    width: row1Gap,
    height: 2,
  }));
  const row2Connectors = [
    {
      left: row2Positions[0].left + BLOCK_WIDTH,
      top: row2Positions[0].top + BLOCK_HEIGHT / 2 - 1,
      width: row2Gap,
      height: 2,
    },
    {
      left: row2Positions[1].left + BLOCK_WIDTH,
      top: row2Positions[1].top + BLOCK_HEIGHT / 2 - 1,
      width: row2LongGap,
      height: 2,
    },
  ];
  const row3Connectors = row3Positions.slice(0, -1).map((pos) => ({
    left: pos.left + BLOCK_WIDTH,
    top: pos.top + BLOCK_HEIGHT / 2 - 1,
    width: row3Gap,
    height: 2,
  }));
  const sideConnectors = [
    {
      left: 0,
      top: row1Positions[0].top + BLOCK_HEIGHT / 2 - 1,
      width: row1LeftOffset,
      height: 2,
    },
    {
      left: row3Positions[1].left + BLOCK_WIDTH,
      top: row3Positions[1].top + BLOCK_HEIGHT / 2 - 1,
      width: CONTAINER_WIDTH - (row3Positions[1].left + BLOCK_WIDTH),
      height: 2,
    },
  ];
  const horizontalConnectors = [
    ...row1Connectors,
    ...row2Connectors,
    ...row3Connectors,
    ...sideConnectors,
  ];

  const arc1 = {
    startX: row1Positions[2].left + BLOCK_WIDTH,
    startY: row1Positions[2].top + BLOCK_HEIGHT / 2,
    endX: row2Positions[2].left + BLOCK_WIDTH,
    endY: row2Positions[2].top + BLOCK_HEIGHT / 2,
    radius: ROW_GAP / 2,
  };
  const arc2 = {
    startX: row2Positions[0].left,
    startY: row2Positions[0].top + BLOCK_HEIGHT / 2,
    endX: row3Positions[0].left,
    endY: row3Positions[0].top + BLOCK_HEIGHT / 2,
    radius: ROW_GAP / 2,
  };

  // --------------------
  // Mobile Layout Constants
  // --------------------
  const MOBILE_CONTAINER_WIDTH = 480;
  const MOBILE_ROW_GAP = 60;
  const MOBILE_BLOCK_GAP = 40;

  const mobilePositions: Array<BlockPositionMobile> = [
    // Row 1 (two columns)
    {
      top: 0,
      left: 0,
      icon: IconResearchPaper,
      label: m.features_upload_research_papers(),
    },
    {
      top: 0,
      left: BLOCK_WIDTH + MOBILE_BLOCK_GAP,
      icon: IconBlogs,
      label: m.features_upload_blogs(),
    },
    // Row 2
    {
      top: BLOCK_HEIGHT + MOBILE_ROW_GAP,
      left: 0,
      icon: IconPDFs,
      label: m.features_upload_pdfs(),
    },
    {
      top: BLOCK_HEIGHT + MOBILE_ROW_GAP,
      left: BLOCK_WIDTH + MOBILE_BLOCK_GAP,
      icon: IconTextbooks,
      label: m.features_upload_textbooks(),
    },
    // Row 3
    {
      top: 2 * (BLOCK_HEIGHT + MOBILE_ROW_GAP),
      left: 0,
      icon: IconDrive,
      label: m.features_upload_drive(),
    },
    {
      top: 2 * (BLOCK_HEIGHT + MOBILE_ROW_GAP),
      left: BLOCK_WIDTH + MOBILE_BLOCK_GAP,
      icon: IconWebsite,
      label: m.features_upload_websites(),
    },
    // Row 4
    {
      top: 3 * (BLOCK_HEIGHT + MOBILE_ROW_GAP),
      left: 0,
      icon: IconScan,
      label: m.features_upload_upload(),
    },
    {
      top: 3 * (BLOCK_HEIGHT + MOBILE_ROW_GAP),
      left: BLOCK_WIDTH + MOBILE_BLOCK_GAP,
      icon: IconPdfUpload,
      label: m.features_upload_upload(),
    },
  ];

  // Helper functions for column layout (below sm)
  function columnPositions(): Array<BlockPositionMobile> {
    const gap = 24; // vertical gap between blocks in column layout
    return mobilePositions.map((block, index) => ({
      ...block,
      top: index * (BLOCK_HEIGHT + gap),
      left: 0,
    }));
  }
  function columnPositionsHeight(): number {
    const gap = 24;
    return mobilePositions.length * (BLOCK_HEIGHT + gap) - gap;
  }

  return (
    <section
      aria-labelledby='features-upload-title'
      className='relative mx-auto flex max-w-[1600px] flex-col overflow-hidden px-4 py-12 sm:px-6 lg:px-8'
    >
      {/* Heading */}
      <div className='flex flex-col gap-4'>
        <span className='w-fit rounded-full bg-linear-to-r from-pink-100 to-blue-100 px-4 py-2 text-base font-bold sm:text-xl'>
          {m.features_upload_badge()}
        </span>
        <h2
          className='text-xl font-bold text-navy-220/70 sm:text-4xl lg:text-5xl'
          id='features-upload-title'
        >
          {m.features_upload_title()}
        </h2>
      </div>

      {/* Mobile Layout for screens between sm and lg (two-column, no lines) */}
      <div
        aria-label='Upload features diagram - tablet view'
        className='mt-8 hidden sm:block lg:hidden'
        role='region'
      >
        <div
          className='relative mx-auto overflow-visible'
          style={{
            width: MOBILE_CONTAINER_WIDTH,
            height: `${String(4 * (BLOCK_HEIGHT + MOBILE_ROW_GAP) - MOBILE_ROW_GAP)}px`,
            maxWidth: 'calc(100% - 80px)', // Account for the side margins
            marginLeft: 'auto',
            marginRight: 'auto',
          }}
        >
          <svg
            className='absolute top-0 left-[-40px] z-0 w-[calc(100%+80px)]'
            preserveAspectRatio='none'
            viewBox={`-40 0 ${String(MOBILE_CONTAINER_WIDTH + 80)} ${String(4 * (BLOCK_HEIGHT + MOBILE_ROW_GAP) - MOBILE_ROW_GAP)}`}
          >
            <GradientDefinitions
              horizontalId='mobileHorizontalGradient'
              verticalId='mobileVerticalGradient'
            />

            {/* Existing horizontal connectors */}
            {mobilePositions.map((block, idx) => {
              if (idx % 2 === 0 && idx < mobilePositions.length - 1) {
                return (
                  <rect
                    className='fill-[url(#mobileHorizontalGradient)]'
                    height={2}
                    key={`mobile-line-${String(idx)}`}
                    style={{ vectorEffect: 'non-scaling-stroke' }}
                    width={MOBILE_BLOCK_GAP}
                    x={BLOCK_WIDTH}
                    y={block.top + BLOCK_HEIGHT / 2 - 1}
                  />
                );
              }
              return null;
            })}

            {/* Snake-like vertical connectors between rows */}
            <path
              className='fill-none stroke-[url(#mobileVerticalGradient)]'
              d={`
                M ${String(BLOCK_WIDTH + MOBILE_BLOCK_GAP + BLOCK_WIDTH)} ${String(BLOCK_HEIGHT / 2)}
                C ${String(BLOCK_WIDTH + MOBILE_BLOCK_GAP + BLOCK_WIDTH + 40)} ${String(BLOCK_HEIGHT / 2)},
                  ${String(BLOCK_WIDTH + MOBILE_BLOCK_GAP + BLOCK_WIDTH + 40)} ${String(BLOCK_HEIGHT + MOBILE_ROW_GAP + BLOCK_HEIGHT / 2)},
                  ${String(BLOCK_WIDTH + MOBILE_BLOCK_GAP + BLOCK_WIDTH)} ${String(BLOCK_HEIGHT + MOBILE_ROW_GAP + BLOCK_HEIGHT / 2)}
                M ${String(BLOCK_WIDTH + MOBILE_BLOCK_GAP + BLOCK_WIDTH)} ${String(2 * (BLOCK_HEIGHT + MOBILE_ROW_GAP) + BLOCK_HEIGHT / 2)}
                C ${String(BLOCK_WIDTH + MOBILE_BLOCK_GAP + BLOCK_WIDTH + 40)} ${String(2 * (BLOCK_HEIGHT + MOBILE_ROW_GAP) + BLOCK_HEIGHT / 2)},
                  ${String(BLOCK_WIDTH + MOBILE_BLOCK_GAP + BLOCK_WIDTH + 40)} ${String(3 * (BLOCK_HEIGHT + MOBILE_ROW_GAP) + BLOCK_HEIGHT / 2)},
                  ${String(BLOCK_WIDTH + MOBILE_BLOCK_GAP + BLOCK_WIDTH)} ${String(3 * (BLOCK_HEIGHT + MOBILE_ROW_GAP) + BLOCK_HEIGHT / 2)}
              `}
              style={{ vectorEffect: 'non-scaling-stroke', strokeWidth: '2' }}
            />

            {/* Left side vertical connector between rows */}
            <path
              className='fill-none stroke-[url(#mobileVerticalGradient)]'
              d={`
                M 0 ${String(BLOCK_HEIGHT + MOBILE_ROW_GAP + BLOCK_HEIGHT / 2)}
                C -40 ${String(BLOCK_HEIGHT + MOBILE_ROW_GAP + BLOCK_HEIGHT / 2)},
                  -40 ${String(2 * (BLOCK_HEIGHT + MOBILE_ROW_GAP) + BLOCK_HEIGHT / 2)},
                  0 ${String(2 * (BLOCK_HEIGHT + MOBILE_ROW_GAP) + BLOCK_HEIGHT / 2)}
              `}
              style={{ vectorEffect: 'non-scaling-stroke', strokeWidth: '2' }}
            />
          </svg>

          <div className='relative z-10'>
            {mobilePositions.map((block) => (
              <FeatureBlock
                height={BLOCK_HEIGHT}
                icon={block.icon}
                key={block.label}
                label={block.label}
                left={block.left}
                top={block.top}
                width={BLOCK_WIDTH}
              />
            ))}
          </div>
        </div>
      </div>

      {/* Mobile Layout for screens below sm (column layout, no lines) */}
      <div className='mt-8 block sm:hidden'>
        <div
          className='relative mx-auto overflow-visible'
          style={{
            width: BLOCK_WIDTH,
            height: `${String(columnPositionsHeight())}px`,
            maxWidth: 'calc(100% - 80px)',
            marginLeft: 'auto',
            marginRight: 'auto',
          }}
        >
          <svg
            className='absolute top-0 left-[-40px] z-0 w-[calc(100%+80px)]'
            preserveAspectRatio='none'
            viewBox={`-40 0 ${String(BLOCK_WIDTH + 80)} ${String(columnPositionsHeight())}`}
          >
            {/* Alternating left and right curved connectors between blocks */}
            {columnPositions().map((block, idx) => {
              if (idx < columnPositions().length - 1) {
                const nextBlock = columnPositions()[idx + 1];
                if (nextBlock) {
                  const isLeft = idx % 2 === 0;
                  const curveOffset = isLeft ? -40 : BLOCK_WIDTH + 40;
                  const startX = isLeft ? 0 : BLOCK_WIDTH;
                  const controlX = curveOffset;

                  return (
                    <path
                      className='fill-none stroke-azure-595 stroke-2'
                      d={`
                        M ${String(startX)} ${String(block.top + BLOCK_HEIGHT / 2)}
                        C ${String(controlX)} ${String(block.top + BLOCK_HEIGHT / 2)},
                          ${String(controlX)} ${String(nextBlock.top + BLOCK_HEIGHT / 2)},
                          ${String(startX)} ${String(nextBlock.top + BLOCK_HEIGHT / 2)}
                      `}
                      key={`column-connector-${String(idx)}`}
                      style={{ vectorEffect: 'non-scaling-stroke' }}
                    />
                  );
                }
              }
              return null;
            })}
          </svg>

          <div className='relative z-10'>
            {columnPositions().map((block, idx) => (
              <FeatureBlock
                height={BLOCK_HEIGHT}
                icon={block.icon}
                key={`${block.label}-${String(idx)}`}
                label={block.label}
                left={block.left}
                top={block.top}
                width={BLOCK_WIDTH}
              />
            ))}
          </div>
        </div>
      </div>

      {/* Desktop Layout (for lg+ screens) */}
      <div
        aria-label='Upload features diagram - desktop view'
        className='relative mt-12 hidden w-full overflow-hidden lg:block'
        role='region'
      >
        <div
          className='relative mx-auto w-full max-w-[1600px]'
          style={{ height: `${String(DESKTOP_HEIGHT)}px` }}
        >
          <svg
            className='absolute top-0 left-0 z-0 size-full'
            preserveAspectRatio='none'
            viewBox={`0 0 ${String(CONTAINER_WIDTH)} ${String(DESKTOP_HEIGHT)}`}
          >
            <GradientDefinitions
              horizontalId='horizontalGradient'
              includeMiddleStop
              verticalId='verticalGradient'
            />

            {horizontalConnectors.map((line, idx) => (
              <rect
                className='fill-[url(#horizontalGradient)]'
                height={line.height}
                key={`h-line-${String(idx)}`}
                style={{ vectorEffect: 'non-scaling-stroke' }}
                width={line.width}
                x={line.left}
                y={line.top}
              />
            ))}
            <path
              className='fill-none stroke-[url(#verticalGradient)]'
              d={`
                M ${String(arc1.startX)} ${String(arc1.startY)}
                A ${String(arc1.radius)} ${String(arc1.radius)} 0 0 1 ${String(arc1.endX)} ${String(arc1.endY)}
              `}
              style={{ vectorEffect: 'non-scaling-stroke', strokeWidth: '2' }}
            />
            <path
              className='fill-none stroke-[url(#verticalGradient)]'
              d={`
                M ${String(arc2.startX)} ${String(arc2.startY)} 
                A ${String(arc2.radius)} ${String(arc2.radius)} 0 0 0 ${String(arc2.endX)} ${String(arc2.endY)}
              `}
              style={{ vectorEffect: 'non-scaling-stroke', strokeWidth: '2' }}
            />
          </svg>

          <div className='absolute top-0 left-0 z-5 h-full w-24 bg-linear-to-r from-white to-transparent' />
          <div className='absolute top-0 right-0 z-5 h-full w-[200px] bg-linear-to-l from-white to-transparent' />

          <div className='relative z-10'>
            {BLOCK_POSITIONS.map((block) => (
              <FeatureBlock
                height={BLOCK_HEIGHT}
                icon={block.icon}
                isDesktop
                key={block.label}
                label={block.label}
                left={toPercent(block.left)}
                top={block.top}
                width={toPercent(BLOCK_WIDTH)}
              />
            ))}
          </div>
        </div>
      </div>

      {/* Blue Gradient Overlay */}
      <div
        aria-hidden='true'
        className='pointer-events-none absolute inset-x-0 bottom-0 -z-10 w-full overflow-hidden'
        style={{ height: '1000px' }}
      >
        <div
          className='absolute inset-x-0 bottom-0 h-[800px] w-full'
          style={{
            background: `
              radial-gradient(
                ellipse at 50% 100%, 
                rgba(5, 135, 251, 0.6) 0%, 
                rgba(5, 135, 251, 0.2) 50%, 
                rgba(5, 135, 251, 0) 75%
              )
            `,
            transform: 'translateY(100px)',
            opacity: 0.25,
            mixBlendMode: 'multiply',
          }}
        />
      </div>
    </section>
  );
}
